<script>
export const pageName = 'map-view-task';
export const pageRoute = '/model-achievement-programme/task/{taskId}';
export const title = 'Model Achievement Programme'
export const requireAuth = false

import { getCurrentUserId } from '../../js/auth.js';
import { listenToUserView } from '../../js/readModel.js';
import { getMapTask } from '../../js/utils/map.js';
import MapFlightCardUrl from '../../resources/MAP-and-Record-flight-card-A4-v3-1.pdf'

export default {
	data() {
		return {
			currentUser: null,
			task: null,
			backgrounds: {},
			MapFlightCardUrl,
			ready: false
		}
	},

	methods: {
		async boot(opts) {

			this.task = await getMapTask(decodeURIComponent(opts.taskId));
			
			// allow anon visitors from main website
			if(getCurrentUserId())
			{
				await listenToUserView(getCurrentUserId(), async user => {
					this.currentUser = user;
				});
			}

			
			this.ready = true;
		}
	}
}

</script>
<template>
	<div v-if="ready" class="container py-5">
		<h1 class="display-4 text-center mb-3">{{ task.name }}</h1>
		<p class="text-muted text-center mb-0">{{ task.desc }}</p>

		<div class="row justify-content-center">
			<div class="col-12 col-md-8 my-5">
				<div class="text-center">
					<div class="d-inline-block" :style="`background: url(${task.bg});}`">
						<img :src="task.icon" />
					</div>
				</div>
				
				<div v-if="currentUser?.map.hasCompletedTask(task.id)" class="alert alert-success">
					<h4 v-if="task.hydrazine" class="mb-0">You have already completed this task but may repeat it to earn more Hydrazine.</h4>
					<h4 v-else class="mb-0">You have already completed this task.</h4>
				</div>

				<div v-if="task.specialFull" class="alert alert-primary">
					<p class="mb-0"><span class="fe fe-info"></span> {{ task.specialFull }}</p>
				</div>

				<div class="card">
					<div class="card-header">
						<h4 class="card-header-title">Overview</h4>
					</div>
					<div class="card-body">
					<div class="row align-items-center mb-4">
						<div class="col-auto text-center">
							<div :style="`background: url(${task.bg}); width: 106px; height: 113px`">
								<div style="padding-top: 38px" class="text-center text-white w-100 h-100">
									<h3 class="mb-0">+{{ task.xp || task.hydrazine }}</h3>
									<span class="small">{{ task.xp ? 'XP' : 'Hydrazine' }}</span>
								</div>
							</div>
							<h3>{{ task.subjectName }}</h3>
						</div>
						<div class="col">
							<p>This task is worth {{task.xp ? `${task.xp} XP` : `${task.hydrazine} Hydrazine`}}.</p>
							<p v-if="task.hydrazine" class="mb-0">You can complete this task as many times as you like and repeatedly earn Hydrazine.</p>
							<p v-else class="mb-0">You can only earn XP once for this task, but feel free to repeat it in combination with other incomplete or Hydrazine-earning tasks.</p>
							
						</div>
					</div>
						
						<p class="mb-0"><a href="${MapFlightCardUrl}" target="_blank">Download the MAP Flight Card <span class="fe fe-download"></span></a> for use in the field.</p>
					</div>
				</div>
				<div>
					<div class="card">
						<div class="card-header">
							<h4 class="card-header-title">Summary</h4>
						</div>
						<div class="card-body">
							<p class="mb-0">{{task.fullDesc || task.desc}}</p>
							
							<div v-if="task.disambiguation" class="alert alert-light mb-0 mt-4">
								<p class="mb-0"><span class="fe fe-info"></span> {{ task.disambiguation }}</p>
							</div>
						</div>
					</div>
					<div class="card border-left-success border-right-success">
						<div class="card-header">
							<h4 class="card-header-title">Pass</h4>
						</div>
						<div class="card-body">						
							<p>By meeting all of the following criteria:</p>
							<ul class="mb-0">
								<li v-for="rule in task.rules.pass" class="mb-2">{{ rule }}</li>
								<li class="mb-2">The rocket must be inspected by the RSO on duty prior to launch and meet the <a href="https://ukra.org.uk/safety/" target="_blank">safety and stability requirements <span class="fe fe-external-link"></span></a></li>
								<li>The flight must be successful and the rocket recovered in a state ready to fly again without major repairs</li>
							</ul>
						</div>
					</div>
					<div v-if="task.rules.options && task.rules.options.length" class="card border-left-secondary border-right-secondary">
						<div class="card-header">
							<h4 class="card-header-title">Options</h4>
						</div>
						<div class="card-body">
							<p>You have the following options:</p>
							<ul class="mb-0">
								<li v-for="rule in task.rules.options" class="mb-2">{{ rule }}</li>
							</ul>
						</div>
					</div>
					<div class="card border-left-danger border-right-danger">
						<div class="card-header">
							<h4 class="card-header-title">Fail</h4>
						</div>
						<div class="card-body">
							<p>If any of the following occur/are true:</p>
							<ul class="mb-0">
								<li v-for="rule in task.rules.fail" class="mb-2">{{ rule }}</li>
								<li class="mb-2">The rocket is not built or flown in accordance with the <a href="https://ukra.org.uk/safety/safety-code/" target="_blank">UKRA Safety Code <span class="fe fe-external-link"></span></a></li>
								<li class="mb-2">The flight is severely unstable or unsafe in any way</li>
								<li class="mb-2">The rocket is recovered in a damaged state such that it cannot fly again without non-trivial repairs</li>
								<li class="mb-2">All parts of the rocket are not recovered (except for exceptional circumstances, like landing in a tree)</li>
								<li>The MAP Assessor does not believe the flyer performed the task themselves. Exceptions are made for young flyers or those with disabilities who need assistance. In these cases, assistants should operate under the flyer's instruction.</li>
							</ul>
						</div>
					</div>
				</div>
				<div class="card">
					<div class="card-header">
						<h4 class="card-header-title">Help and Inspiration</h4>
					</div>
					<div class="card-body">
						<div v-for="help in task.help">
							<p v-if="help.startsWith('blog:')"><a :href="`https://ukra.org.uk/tag/${help.substr(5)}/`" target="_blank">View our blog articles <span class="fe fe-external-link"></span></a></p>
							<iframe v-else-if="help.indexOf('youtube.com') > -1 && help.indexOf('embed') > -1" width="100%" height="315" :src="help" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
							<iframe v-else-if="help.indexOf('printables.com') > -1 && help.indexOf('embed') > -1" :src="help" width="100%" height="445" style="max-width: 400px;" scrolling="no" frameborder="0"></iframe>
							<p v-else-if="help.startsWith('http')"><a :href="help" target="_blank">{{help}} <span class="fe fe-external-link"></span></a></p>
							<p v-else>{{ help }}</p>
						</div>
						<p v-if="!task.help?.length" class="mb-0">Help content is coming soon. Feel free to <a href="mailto:membership@ukra.org.uk">submit any useful content</a> you may be aware of. Until then, we recommend searching on <a href="https://www.rocketryforum.com/" target="_blank">The Rocketry Forum <span class="fe fe-external-link"></span></a>.</p>
					</div>
					<div class="card-footer">
						<p class="text-muted small mb-0">UKRA is not responsible for the content on other websites. Please <a href="mailto:membership@ukra.org.uk">let us know</a> if you find any broken content or other issues. UKRA does not endorse any product or service.</p>
					</div>
				</div>
			</div>
		</div>
		

		
		
    </div>
</template>
